(() => {
  'use strict';
  const VIEW = 'fs-inline-data';
  const positiveId = value => Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const path = url => url.pathname.replace(/\/$/, '') || '/';
  const sameOrigin = value => {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid storefront URL');
    return url;
  };
  const parsePayload = (text, kind) => {
    if (typeof text !== 'string' || text.length > 4000000) throw new Error('Invalid response');
    const nodes = new DOMParser().parseFromString(text, 'text/html').querySelectorAll('script#fs-inline-data[type="application/json"]');
    if (nodes.length !== 1) throw new Error('Missing storefront payload');
    const data = JSON.parse(nodes[0].textContent);
    if (data?.schema !== 1 || data.kind !== kind) throw new Error('Unexpected storefront payload');
    return data;
  };
  const safeImage = value => {
    if (!value) return null;
    try { const url = new URL(value, location.origin); return url.protocol === 'https:' ? url.href : null; } catch (_) { return null; }
  };
  class InlineCategories {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(this.el('config').textContent);
      this.panel = this.el('panel');
      this.modal = root.hasAttribute('data-fsic-modal') && typeof this.panel.showModal === 'function';
      this.version = 0;
      this.page = 0;
      this.ids = new Set();
      this.categoryPage = this.config.categoriesPage;
      this.categoryNext = null;
      if (this.config.categoriesNext) {
        const cursor = sameOrigin(this.config.categoriesNext);
        const url = sameOrigin(this.config.collectionsUrl);
        const key = this.config.pageParam || 'page';
        url.searchParams.set(key, cursor.searchParams.get(key) || String(this.categoryPage + 1));
        this.categoryNext = this.endpoint(url.href);
      }
      this.el('categories-more').hidden = !this.categoryNext;
      this.el('all-collections').hidden = true;
      this.root.querySelectorAll('[data-fsic-category]').forEach(link => this.enhanceLink(link));
      this.onClick = event => this.click(event);
      root.addEventListener('click', this.onClick, true);
      if (this.modal) this.setupModal();
      this.onMySize = () => this.ensureSizeCatalog();
      document.addEventListener('fs:my-size-change', this.onMySize);
      if (this.config.autoDefault) {
        const initial = this.config.initial;
        const links = [...this.root.querySelectorAll('[data-fsic-category]')];
        const link = initial && links.find(item => item.dataset.fsicCategory === initial.handle);
        const cards = [...this.el('grid').querySelectorAll('[data-fhg-card]')];
        if (link && cards.length && typeof initial.title === 'string' && Number.isSafeInteger(initial.total) && initial.total >= cards.length) {
          this.active = { handle: initial.handle, title: initial.title, url: this.preview(initial.url), link };
          this.ids = new Set(cards.map(card => card.dataset.fhgCard));
          this.total = initial.total;
          this.panel.hidden = false;
          link.classList.add('is-active'); link.setAttribute('aria-expanded', 'true');
          if (typeof this.grid?.dialog?.showModal === 'function') cards.forEach(card => {
            const button = card.querySelector('[data-fsic-plus]');
            if (button) { button.dataset.fhgPlus = button.dataset.fsicPlus; button.hidden = false; }
          });
        } else if (links[0]) this.select(links[0]);
      }
      if (this.config.progressive && typeof window.IntersectionObserver === 'function') {
        this.pageObserver = new window.IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.loadNearEnd();
        }, { root: this.modal ? this.panel : null, rootMargin: '700px 0px', threshold: 0 });
      }
      queueMicrotask(() => { this.notifySizeCatalog(); this.ensureSizeCatalog(); });
    }
    loadNearEnd() {
      if (this.destroyed || this.panel.hidden || this.failedURL || this.loading || !this.next) return;
      this.loadProducts(this.next);
    }
    observeNextPage() {
      this.pageObserver?.disconnect();
      if (!this.destroyed && !this.panel.hidden && !this.failedURL && this.next) this.pageObserver?.observe(this.el('more'));
    }
    notifySizeCatalog() {
      window.FSMySize?.registerInline(this.root, {complete: this.page > 0 && !this.next && this.ids.size === this.total, total:this.total, viewAllURL:this.active?.url});
    }
    ensureSizeCatalog() {
      if (!window.FSMySize?.get() || !this.active || this.loading || this.failedURL || this.destroyed || this.panel.hidden) return;
      const next = this.page === 0 ? this.endpoint(this.active.url) : this.next;
      if (next) this.loadProducts(next);
    }
    get grid() { return this.root.hasAttribute('data-fsic-direct-cart') ? this.root.closest('[data-fs-live-collection]')?.fsCollectionSelection : this.panel.fhg; }
    get quickview() { return this.root.closest('[data-fs-live-collection]')?.querySelector('fs-live-quickview'); }
    busy() {
      const html = document.documentElement;
      return Boolean(this.grid?.pending || this.quickview?.pending || html.hasAttribute('data-fs-quickview-cart-pending') || html.hasAttribute('data-fs-selection-cart-pending'));
    }
    setupModal() {
      this.root.querySelectorAll('[data-fsb-collection]').forEach(link => {
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', this.panel.id);
      });
      this.onCancel = event => { event.preventDefault(); this.close(); };
      this.onModalClose = () => { if (!this.panel.open) this.finishModalClose(); };
      this.onPointerDown = event => { this.backdropDown = event.target === this.panel; };
      this.onModalHistory = event => {
        const state = window.history.state;
        if (this.historyReturn && location.href === this.historyReturn.url && JSON.stringify(state) === this.historyReturn.state) {
          this.historyReturn = null;
          const queued = this.queuedCategory; this.queuedCategory = null;
          if (queued?.isConnected && !this.destroyed) this.select(queued);
          event.stopImmediatePropagation();
          return;
        }
        if (!this.historyKey || state?.__fsCategory === this.historyKey) return;
        if (this.busy() && location.href === this.historyURL) {
          window.history.pushState(this.historyState, '', this.historyURL);
          this.grid?.status('cart_busy');
          event.stopImmediatePropagation();
          return;
        }
        this.historyKey = null;
        this.close(true);
      };
      this.panel.addEventListener('cancel', this.onCancel);
      this.panel.addEventListener('close', this.onModalClose);
      this.panel.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('popstate', this.onModalHistory, true);
    }
    openModal() {
      if (!this.modal || this.panel.open) return;
      const body = document.body; const html = document.documentElement;
      this.scrollLock = { body: body.style.overflow, html: html.style.overflow, x: window.scrollX, y: window.scrollY };
      this.panel.hidden = false;
      this.panel.showModal();
      body.style.overflow = 'hidden'; html.style.overflow = 'hidden';
      this.panel.scrollTop = this.modalScroll || 0;
      this.el('title').focus({ preventScroll: true });
      if (!this.historyKey && (!window.history.state || typeof window.history.state === 'object' && !Array.isArray(window.history.state))) {
        try {
          this.historyBaseState = JSON.stringify(window.history.state);
          this.historyURL = location.href;
          this.historyKey = 'fs-category-' + Date.now() + '-' + Math.random().toString(36).slice(2);
          this.historyState = { ...window.history.state, __fsCategory: this.historyKey };
          window.history.pushState(this.historyState, '', this.historyURL);
        } catch (_) { this.historyKey = null; }
      }
      const child = this.quickview?.dialog;
      if (child && child !== this.childDialog) {
        this.childDialog?.removeEventListener('close', this.onChildClose);
        this.childDialog = child;
        this.onChildClose = () => {
          if (this.closeAfterChild) { this.closeAfterChild = false; this.close(true); }
          else if (this.panel.open && !this.destroyed) {
            document.body.style.overflow = 'hidden'; document.documentElement.style.overflow = 'hidden';
          }
        };
        child.addEventListener('close', this.onChildClose);
      }
    }
    finishModalClose() {
      if (!this.scrollLock) return;
      const key = this.historyKey; this.historyKey = null;
      if (key && window.history.state?.__fsCategory === key) {
        this.historyReturn = { url: this.historyURL, state: this.historyBaseState };
        window.history.back();
      }
      const lock = this.scrollLock; this.scrollLock = null;
      document.body.style.overflow = lock.body; document.documentElement.style.overflow = lock.html;
      window.scrollTo?.({ left: lock.x, top: lock.y, behavior: 'instant' });
      this.returnFocus();
    }
    el(name) { return this.root.querySelector(`[data-fsic-${name}]`); }
    text(key, values = {}) { return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), this.config.strings[key] || ''); }
    preview(value) {
      const url = sameOrigin(value);
      if (/^\d+$/.test(this.root.dataset.previewThemeId || '')) url.searchParams.set('preview_theme_id', this.root.dataset.previewThemeId);
      return url.href;
    }
    endpoint(value) {
      const url = sameOrigin(value);
      url.searchParams.set('view', VIEW);
      url.hash = '';
      return this.preview(url.href);
    }
    nextURL(next, current) {
      if (next == null || next === '') return null;
      const url = sameOrigin(next);
      if (path(url) !== path(sameOrigin(current))) throw new Error('Invalid pagination');
      return this.endpoint(url.href);
    }
    enhanceLink(link) {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-controls', this.panel.id);
      link.setAttribute('aria-expanded', 'false');
      if (this.modal) link.setAttribute('aria-haspopup', 'dialog');
      link.addEventListener('keydown', event => { if (event.key === ' ') { event.preventDefault(); link.click(); } });
    }
    async request(url, kind, controller) {
      const requested = sameOrigin(url);
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { method: 'GET', credentials: 'same-origin', mode: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal: controller.signal });
        const finalURL = sameOrigin(response.url || url);
        if (!response.ok || path(finalURL) !== path(requested) || (finalURL.searchParams.has('view') && finalURL.searchParams.get('view') !== VIEW)) throw new Error('Unavailable storefront response');
        return parsePayload(await response.text(), kind);
      } finally { clearTimeout(timer); }
    }
    click(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !this.root.contains(target)) return;
      if (this.modal) {
        const strip = target.closest('[data-fsb-collection]');
        if (strip && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!event.button || event.button === 0)) {
          event.preventDefault(); event.stopImmediatePropagation(); this.select(strip); return;
        }
        const product = target.closest('[data-fs-live-quickview]');
        if (product && this.panel.contains(product) && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!event.button || event.button === 0)) {
          if (this.busy()) { event.preventDefault(); event.stopImmediatePropagation(); this.grid?.status('cart_busy'); return; }
          if (typeof this.quickview?.dialog?.showModal === 'function') { event.preventDefault(); event.stopImmediatePropagation(); this.quickview.open(product); this.childHistoryKey = this.quickview.historyKey; return; }
        }
        if (target === this.panel && this.backdropDown) {
          this.backdropDown = false;
          const rect = this.panel.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
      }
      const plus = target.closest('[data-fsic-plus]');
      if (plus) { if (this.root.hasAttribute('data-fsic-direct-cart')) return; event.preventDefault(); event.stopImmediatePropagation(); this.pick(plus); return; }
      const category = target.closest('[data-fsic-category]');
      if (category && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && (!event.button || event.button === 0)) { event.preventDefault(); this.select(category); return; }
      if (target.closest('[data-fsic-categories-more]')) this.moreCategories();
      if (target.closest('[data-fsic-more]')) this.loadProducts(this.next);
      if (target.closest('[data-fsic-retry]')) this.loadProducts(this.failedURL);
      if (target.closest('[data-fsic-close]')) this.close();
    }
    async moreCategories() {
      if (this.categoryLoading || !this.categoryNext) return;
      this.categoryLoading = true;
      const button = this.el('categories-more');
      button.disabled = true;
      this.el('category-status').textContent = this.text('loading_categories');
      const controller = new AbortController(); this.categoryController = controller;
      try {
        const data = await this.request(this.categoryNext, 'collections', controller);
        if (!this.root.isConnected || controller.signal.aborted) return;
        if (!Array.isArray(data.items) || data.items.length > 50 || data.page !== this.categoryPage + 1) throw new Error('Invalid collection page');
        const next = this.nextURL(data.next, this.endpoint(this.config.collectionsUrl));
        const known = new Set([...this.root.querySelectorAll('[data-fsic-category]')].map(link => link.dataset.fsicCategory));
        const links = data.items.map(item => {
          if (!positiveId(item.id) || typeof item.title !== 'string' || typeof item.handle !== 'string' || !item.handle) throw new Error('Invalid collection');
          const url = this.preview(item.url);
          if (known.has(item.handle)) return null;
          known.add(item.handle);
          const link = document.createElement('a'); link.href = url; link.textContent = item.title; link.dataset.fsicCategory = item.handle; this.enhanceLink(link); return link;
        }).filter(Boolean);
        links.forEach(link => button.before(link));
        this.categoryPage = data.page; this.categoryNext = next;
        button.hidden = !next;
        this.el('category-status').textContent = '';
        this.el('all-collections').hidden = true;
        if (!next) (links[0] || this.root.querySelector('[data-fsic-category]'))?.focus({ preventScroll: true });
      } catch (_) {
        if (this.root.isConnected && !this.destroyed) { this.el('category-status').textContent = this.text('categories_error'); this.el('all-collections').hidden = false; }
      } finally { this.categoryLoading = false; button.disabled = false; }
    }
    select(link) {
      if (this.modal && this.historyReturn) { this.queuedCategory = link; return; }
      if (this.modal ? this.busy() : this.grid?.pending) { this.grid?.status('cart_busy'); return; }
      const handle = link.dataset.fsicCategory || link.dataset.fsbCollection;
      if (this.modal && this.active?.handle === handle && (this.page || this.failedURL)) {
        this.active.link = link; this.panel.hidden = false; this.markActive(handle); this.openModal();
        this.observeNextPage(); this.ensureSizeCatalog(); return;
      }
      this.productController?.abort(); this.pickController?.abort(); this.grid?.close();
      this.pageObserver?.disconnect();
      this.version += 1; this.loading = false; this.picking = null;
      this.active = { handle, title: link.textContent.trim(), url: this.preview(link.href), link };
      this.el('results').style.minHeight = Math.min(600, Math.max(240, this.el('results').getBoundingClientRect().height)) + 'px';
      this.panel.hidden = false;
      this.markActive(handle);
      this.el('title').textContent = this.active.title;
      this.el('view-all').href = this.active.url;
      this.el('grid').replaceChildren(); this.ids.clear(); this.page = 0; this.next = null; this.notifySizeCatalog();
      if (this.modal) { this.modalScroll = 0; this.openModal(); this.panel.scrollTop = 0; }
      this.loadProducts(this.endpoint(this.active.url));
    }
    markActive(handle) {
      this.root.querySelectorAll('[data-fsic-category], [data-fsb-collection]').forEach(item => {
        const active = (item.dataset.fsicCategory || item.dataset.fsbCollection) === handle;
        item.classList.toggle('is-active', active); item.setAttribute('aria-expanded', String(active));
        if (this.modal && item.hasAttribute('data-fsb-collection')) { if (active) item.setAttribute('aria-current', 'true'); else item.removeAttribute('aria-current'); }
      });
    }
    async loadProducts(url) {
      if (!url || this.loading || !this.active || this.panel.hidden) return;
      const version = this.version;
      const expectedPage = this.page + 1;
      const controller = new AbortController(); this.productController = controller;
      this.loading = true; this.failedURL = null;
      this.el('results').setAttribute('aria-busy', 'true');
      this.el('status').textContent = this.text('loading', { collection: this.active.title });
      const more = this.el('more'); const retry = this.el('retry');
      const focusedControl = [more, retry].find(button => button === document.activeElement);
      more.hidden = focusedControl !== more; retry.hidden = focusedControl !== retry;
      more.disabled = true; retry.disabled = true;
      try {
        const data = await this.request(url, 'products', controller);
        if (version !== this.version || this.destroyed || controller.signal.aborted) return;
        if (!Array.isArray(data.items) || data.items.length > 24 || data.handle !== this.active.handle || data.page !== expectedPage || !Number.isSafeInteger(data.total) || data.total < 0 || (this.grid && data.currency !== this.grid.config.currency)) throw new Error('Invalid product page');
        const next = this.nextURL(data.next, this.active.url);
        if (next && Number(sameOrigin(next).searchParams.get('page')) !== data.page + 1) throw new Error('Non-progressing product pagination');
        const cards = data.items.map(item => this.card(item));
        if (next && data.items.every(item => this.ids.has(String(item.id)))) throw new Error('No progress in product page');
        const added = [];
        for (let index = 0; index < cards.length; index += 1) {
          const key = String(data.items[index].id);
          if (!this.ids.has(key)) { this.ids.add(key); this.el('grid').append(cards[index]); added.push(cards[index]); }
        }
        this.page = data.page; this.next = next; this.total = data.total;
        this.el('status').textContent = this.ids.size ? this.text('loaded', { count: this.ids.size, total: data.total }) : this.text('empty');
        more.hidden = !next; retry.hidden = true;
        this.grid?.render(); this.notifySizeCatalog();
        if (focusedControl && (focusedControl === retry || !next)) (added[0]?.querySelector('a') || this.el('view-all')).focus({ preventScroll: true });
      } catch (_) {
        if (version === this.version && !this.destroyed) { this.failedURL = url; this.el('status').textContent = this.text('error'); more.hidden = true; retry.hidden = false; retry.disabled = false; if (focusedControl) retry.focus({ preventScroll: true }); }
      } finally {
        if (version === this.version) { this.loading = false; more.disabled = false; retry.disabled = false; this.el('results').setAttribute('aria-busy', 'false'); this.el('results').style.minHeight = ''; queueMicrotask(() => { this.ensureSizeCatalog(); this.observeNextPage(); }); }
      }
    }
    card(item) {
      if (!positiveId(item.id) || typeof item.title !== 'string' || typeof item.priceText !== 'string' || typeof item.available !== 'boolean') throw new Error('Invalid product');
      const url = this.preview(item.url);
      const row = document.createElement('li'); row.className = 'fhg__item'; row.dataset.fhgCard = String(item.id);
      const link = document.createElement('a'); link.className = 'fhg__tile'; link.href = url;
      const photo = document.createElement('div'); photo.className = 'fhg__photo';
      const src = safeImage(item.image);
      if (src) {
        const img = document.createElement('img'); img.src = src; img.alt = ''; img.setAttribute('loading', 'lazy'); img.setAttribute('decoding', 'async'); img.width = 400; img.height = 600;
        img.sizes = this.modal ? '(min-width: 1200px) 270px, 22vw' : '(min-width: 750px) 25vw, 50vw';
        img.srcset = [180, 300, 450, 600].map(width => { const source = new URL(src); source.searchParams.set('width', String(width)); return `${source.href} ${width}w`; }).join(', ');
        photo.append(img);
      } else { const blank = document.createElement('span'); blank.className = 'fhg__no-photo'; blank.textContent = this.text('no_image'); photo.append(blank); }
      const title = document.createElement('h3'); title.textContent = item.title;
      const price = document.createElement('p'); price.className = 'fhg__price'; price.textContent = item.priceText;
      link.append(photo, title, price); row.append(link);
      if (this.modal) {
        link.dataset.fsLiveQuickview = String(item.id); link.dataset.productUrl = url; link.dataset.productTitle = item.title;
        if (src) link.dataset.productImage = src;
        link.setAttribute('aria-haspopup', 'dialog');
        if (this.quickview?.dialog?.id) link.setAttribute('aria-controls', this.quickview.dialog.id);
      }
      if (!item.available) { const sold = document.createElement('span'); sold.className = 'fsic__sold-out'; sold.textContent = this.text('sold_out'); row.append(sold); }
      else if (this.grid && typeof this.grid.dialog?.showModal === 'function') {
        const plus = document.createElement('button'); plus.type = 'button'; plus.className = 'fhg__plus';
        if (this.modal) {
          plus.dataset.fsLiveQuickview = String(item.id); plus.dataset.productUrl = url; plus.dataset.productTitle = item.title;
          if (src) plus.dataset.productImage = src;
        } else { plus.dataset.fhgPlus = String(item.id); plus.dataset.fsicPlus = String(item.id); }
        plus.setAttribute('aria-label', this.text('choose', { product: item.title })); plus.setAttribute('aria-haspopup', 'dialog'); plus.setAttribute('aria-controls', this.modal ? this.quickview.dialog.id : this.grid.dialog.id);
        const symbol = document.createElement('span'); symbol.textContent = '+'; symbol.setAttribute('aria-hidden', 'true');
        const size = document.createElement('span'); size.textContent = this.text('size'); plus.append(symbol, size); row.append(plus);
      }
      if (this.root.hasAttribute('data-fsic-direct-cart')) {
        row.dataset.fsLiveCompactCard = String(item.id);
        if (item.seed) { const seed = document.createElement('script'); seed.type = 'application/json'; seed.dataset.fsCollectionProduct = ''; seed.textContent = JSON.stringify(item.seed); row.append(seed); }
        if (Array.isArray(item.options) && Array.isArray(item.variants)) {
          const sizeIndex = item.options.findIndex(option => /size/i.test(typeof option === 'string' ? option : option.name));
          const meta = {id:item.id,hasSize:sizeIndex >= 0,variants:sizeIndex >= 0 ? item.variants.map(variant=>({id:variant.id,size:variant.options?.[sizeIndex],available:variant.available,matched:true})) : [],rendered:sizeIndex >= 0 ? item.variants.length : 0,total:item.variantsTotal ?? item.variants.length};
          const source = document.createElement('span'); source.hidden = true; source.dataset.fsLiveSizeMeta = JSON.stringify(meta); row.append(source);
        }
      }
      return row;
    }
    async pick(button) {
      if (!this.grid || this.grid.pending || this.picking || this.grid.dialog.open) return;
      const row = button.closest('[data-fhg-card]');
      const link = row?.querySelector('a');
      if (!row || !link) return;
      const version = this.version;
      const controller = new AbortController(); this.pickController = controller; this.picking = button;
      button.disabled = true; button.setAttribute('aria-busy', 'true');
      row.querySelector('.fsic__product-error')?.remove();
      this.el('status').textContent = this.text('loading_options');
      try {
        const data = await this.request(this.endpoint(link.href), 'product', controller);
        if (version !== this.version || !button.isConnected || this.destroyed || controller.signal.aborted) return;
        const product = data.product;
        if (data.currency !== this.grid.config.currency || String(product?.id) !== button.dataset.fhgPlus || !Array.isArray(product?.variants) || product.variants.length > 50 || !Array.isArray(product?.options)) throw new Error('Invalid product options');
        if (!data.complete || product.requires_selling_plan) { this.productError(row, 'product_required', link.href); return; }
        if (path(sameOrigin(product.url)) !== path(sameOrigin(link.href))) throw new Error('Mismatched product URL');
        this.grid.products.set(String(product.id), product);
        this.grid.open(button);
      } catch (_) {
        if (version === this.version && button.isConnected && !this.destroyed) this.productError(row, 'options_error', link.href);
      } finally {
        button.removeAttribute('aria-busy'); button.disabled = Boolean(this.grid.pending);
        if (this.picking === button) this.picking = null;
        if (version === this.version && !this.destroyed) this.el('status').textContent = this.text('loaded', { count: this.ids.size, total: this.total ?? this.ids.size });
      }
    }
    productError(row, key, url) {
      const note = document.createElement('p'); note.className = 'fsic__product-error'; note.setAttribute('role', 'status'); note.textContent = `${this.text(key)} `;
      const link = document.createElement('a'); link.href = url; link.textContent = this.text('product_details'); note.append(link); row.append(note);
    }
    close(fromHistory = false) {
      if (this.modal ? this.busy() : this.grid?.pending) { this.grid?.status('cart_busy'); return; }
      if (this.modal && !fromHistory && this.childHistoryKey && window.history.state?.__fsQuickview === this.childHistoryKey && !this.quickview?.dialog?.open) {
        clearTimeout(this.closeTimer); this.closeTimer = setTimeout(() => { if (!this.destroyed) this.close(); }, 40); return;
      }
      if (this.modal && (this.quickview?.dialog?.open || this.quickview?.scrollLock)) {
        if (fromHistory) { this.closeAfterChild = true; this.quickview.close(); }
        return;
      }
      this.productController?.abort(); this.pickController?.abort(); this.grid?.close();
      this.pageObserver?.disconnect();
      this.version += 1; this.loading = false; this.picking = null;
      if (this.modal) { this.modalScroll = this.panel.scrollTop; if (this.panel.open) this.panel.close(); }
      this.panel.hidden = true; this.el('results').style.minHeight = ''; this.el('results').setAttribute('aria-busy', 'false');
      this.root.querySelectorAll('[data-fsic-category]').forEach(link => { link.setAttribute('aria-expanded', 'false'); link.classList.toggle('is-active', link.dataset.fsicCategory === 'all'); });
      if (this.modal) { this.markActive(null); this.finishModalClose(); } else this.returnFocus();
    }
    returnFocus() {
      const photo = [...this.root.querySelectorAll('[data-fsb-collection]')].find(item => item.dataset.fsbCollection === this.active?.handle);
      const returnTarget = this.active?.link.closest('[hidden]') ? photo : this.active?.link;
      returnTarget?.focus({ preventScroll: true });
    }
    destroy() {
      this.pageObserver?.disconnect(); document.removeEventListener('fs:my-size-change', this.onMySize); this.destroyed = true; this.version += 1; this.productController?.abort(); this.pickController?.abort(); this.categoryController?.abort(); this.grid?.close(); this.root.removeEventListener('click', this.onClick, true);
      if (this.modal) {
        clearTimeout(this.closeTimer);
        this.queuedCategory = null;
        if (this.quickview?.returnOpener && this.panel.contains(this.quickview.returnOpener)) { this.quickview.close(); this.quickview.unlockScroll(); }
        if (this.panel.open) this.panel.close();
        this.finishModalClose();
        this.panel.removeEventListener('cancel', this.onCancel); this.panel.removeEventListener('close', this.onModalClose); this.panel.removeEventListener('pointerdown', this.onPointerDown);
        this.childDialog?.removeEventListener('close', this.onChildClose);
        window.removeEventListener('popstate', this.onModalHistory, true);
      }
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { InlineCategories, parsePayload, safeImage, positiveId };
  if (typeof window === 'undefined') return;
  const boot = (scope = document) => scope.querySelectorAll('[data-fs-inline-categories]').forEach(root => {
    if (root.hasAttribute('data-fsic-modal') && typeof root.querySelector('[data-fsic-panel]')?.showModal !== 'function') return;
    if (!root.fsic) { try { root.fsic = new InlineCategories(root); } catch (_) {} }
  });
  if (document.readyState === 'complete') queueMicrotask(() => boot());
  else document.addEventListener('DOMContentLoaded', () => queueMicrotask(() => boot()), { once: true });
  document.addEventListener('shopify:section:load', event => queueMicrotask(() => boot(event.target)));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-fs-inline-categories]').forEach(root => root.fsic?.destroy()));
})();
